<template>
  <div class="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
    <div class="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto m-4">
      <div class="p-6">
        <!-- Header -->
        <div class="flex justify-between items-center mb-6">
          <h2 class="text-2xl font-bold">Use {{ item.name }}</h2>
          <button 
            @click="$emit('close')"
            class="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>

        <!-- Item Info -->
        <div class="mb-4 p-4 bg-gray-50 rounded-lg">
          <div class="flex items-start gap-3">
            <div class="text-2xl">{{ PrizeManager.getItemIcon(item) }}</div>
            <div>
              <h3 class="font-bold">{{ item.name }}</h3>
              <span 
                :style="{ color: PrizeManager.getRarityColor(item.rarity) }"
                class="text-xs font-medium uppercase"
              >
                {{ item.rarity }}
              </span>
              <p class="text-sm text-gray-600 mt-1">{{ item.description }}</p>
            </div>
          </div>
        </div>

        <!-- Chocobo Selection -->
        <div>
          <h3 class="text-lg font-semibold mb-3">Select a Chocobo</h3>
          <div class="space-y-2">
            <div
              v-for="chocobo in eligibleChocobos"
              :key="chocobo.id"
              @click="selectedChocoboId = chocobo.id"
              :class="[
                'p-4 border rounded-lg cursor-pointer transition-colors',
                selectedChocoboId === chocobo.id
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              ]"
            >
              <div class="flex items-center justify-between">
                <div>
                  <h4 class="font-bold">{{ chocobo.name }}</h4>
                  <p class="text-sm text-gray-600">
                    Level {{ chocobo.level }} - {{ chocobo.color }} {{ chocobo.gender }}
                  </p>
                  <div class="flex gap-3 mt-2">
                    <span 
                      :class="[
                        getStatGradeClass(getStatGrade(chocobo.speed)),
                        'px-2 py-1 rounded text-xs font-medium'
                      ]"
                    >
                      Speed: {{ getStatGrade(chocobo.speed) }}
                    </span>
                    <span 
                      :class="[
                        getStatGradeClass(getStatGrade(chocobo.stamina)),
                        'px-2 py-1 rounded text-xs font-medium'
                      ]"
                    >
                      Stamina: {{ getStatGrade(chocobo.stamina) }}
                    </span>
                  </div>
                  
                  <!-- Show active effects if any -->
                  <div v-if="getActiveEffectsCount(chocobo) > 0" class="mt-2">
                    <span class="text-xs text-blue-600">
                      {{ getActiveEffectsCount(chocobo) }} active effect(s)
                    </span>
                  </div>

                  <!-- Show warning for breeding items -->
                  <div v-if="item.category === 'breeding' && chocobo.consumedBreedingItems?.length > 0" class="mt-2">
                    <span class="text-xs text-amber-600">
                      ⚠️ Already consumed {{ chocobo.consumedBreedingItems.length }} breeding item(s)
                    </span>
                  </div>
                </div>
                <input
                  type="radio"
                  :checked="selectedChocoboId === chocobo.id"
                  class="w-4 h-4 text-blue-600"
                />
              </div>
            </div>
          </div>

          <!-- Empty State -->
          <div v-if="eligibleChocobos.length === 0" class="text-center py-8 text-gray-500">
            No chocobos available to use this item on
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="mt-6 flex justify-end space-x-3">
          <button
            @click="$emit('close')"
            class="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            Cancel
          </button>
          <button
            @click="useItem"
            :disabled="!selectedChocoboId"
            :class="[
              'px-4 py-2 rounded font-medium',
              selectedChocoboId
                ? 'bg-blue-500 text-white hover:bg-blue-600'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            ]"
          >
            Use Item
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
/* eslint-disable */
import { ref, computed } from 'vue'
import { useGameStore } from '@/store/game'
import { PrizeManager } from '@/services/prizeManager'
import { EffectManager } from '@/services/effectManager'
import { getStatGrade, getStatGradeClass } from '@/utils/chocoboStats'

const props = defineProps({
  item: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['close', 'item-used'])

const gameStore = useGameStore()
const selectedChocoboId = ref(null)

const eligibleChocobos = computed(() => {
  const playerChocoboIds = gameStore.playerOwner?.chocoboIds || []
  const allChocobos = gameStore.chocobos || []
  const result = []
  
  for (const id of playerChocoboIds) {
    const chocobo = allChocobos.find(c => c.id === id)
    if (chocobo) {
      // Check if item can be used on this chocobo
      const validation = PrizeManager.validateItemUsage(props.item, chocobo)
      if (validation.valid) {
        result.push(chocobo)
      }
    }
  }
  
  return result
})

const getActiveEffectsCount = (chocobo) => {
  // Count active effects without modifying state
  if (!chocobo.activeEffects) return 0
  
  const currentDay = gameStore.currentDay
  return chocobo.activeEffects.filter(effect => {
    return !effect.expiresDay || effect.expiresDay > currentDay
  }).length
}

const useItem = () => {
  if (!selectedChocoboId.value) return

  const chocobo = gameStore.chocobos.find(c => c.id === selectedChocoboId.value)
  if (!chocobo) return

  const success = EffectManager.applyConsumableEffect(chocobo, props.item, gameStore.currentDay)
  
  if (success) {
    emit('item-used', {
      item: props.item,
      chocobo: chocobo
    })
    
    // Save game state
    gameStore.saveGame()
  }
}
</script>